import { useEffect, useMemo, useState } from 'react';
import { Tag, TaggableResource, TaggableResourceType } from '~/types/models/tags';
import {
  ResourceTag
} from '~/types/models/testimonial';

import { unionBy, remove } from 'lodash';
import { getResourceTag } from '~/App/Modules/Testimonials/util';
import { useTagResource, useUntagResource } from '~/App/Api/Tag/hooks';
import { DateTime } from 'luxon';

const useResourceTagging = (
  resource?: TaggableResource,
  resourceType?: TaggableResourceType,
  onTagResourceSuccess?: () => void,
  onUntagResourceSuccess?: () => void,
) => {
  //resource can have multiple tags
  const [resourceTags, setResourceTags] = useState<ResourceTag[]>([]);
  const defaultTags = useMemo(
    () => {
      if (resource?.tags === undefined) {
        return [];
      }

      return resource.tags.map<Tag>(({ updated_at, tag }) => ({
        ...tag,
        selected_at: DateTime.fromISO(updated_at).toMillis(),
      }));
    },
    [resource]
  );

  const { mutateAsync: tagResponseMutation } = useTagResource({
    onSuccess: ({ data: resourceTag }) => {
      setResourceTags(unionBy([resourceTag], resourceTags, 'uuid'));
      onTagResourceSuccess?.();
    },
  });

  const { mutateAsync: untagResponseMutation } = useUntagResource({
    onSuccess: ({ data: deleteResourceTag }) => {
      setResourceTags(
        remove(
          resourceTags,
          (resourceTag) => resourceTag.uuid !== deleteResourceTag.uuid
        )
      );
      onUntagResourceSuccess?.();
    },
  });

  const deleteTag = (tag: Tag) => {
    const resourceTag = getResourceTag(tag, resourceTags);
    if (resourceTag) {
      void untagResponseMutation({
        resourceTagUuid: resourceTag.uuid,
      });
    }
  };

  const addTag = (tag: Tag) => {
    if(!resource || !resourceType) return;
    void tagResponseMutation({
      resourceUuid: resource?.uuid,
      tagUuid: tag.uuid,
      resourceType: resourceType,
    });
  };

  useEffect(() => {
    if (resource?.tags?.length) {
      setResourceTags(resource.tags);
    }
  }, [resource]);

  return {
    addTag,
    deleteTag,
    defaultTags,
  };
};

export default useResourceTagging;
